import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import { route } from 'preact-router';
import PubSub, { topics } from '../../lib/pubsub';
import SaveButton from '../article/saveButton';
import { MARKETPLACE_CATEGORIES, marketplaceCategoryLabel, isNewListing, isFinnUrl } from '../../lib/marketplaceCategories';
import { decodeFinnImport } from '../../lib/finnBookmarklet';
import { loadFinnQueue, saveFinnQueue, mergeFinnQueue } from '../../lib/finnImportQueue';

const SEARCH_DEBOUNCE_MS = 400;
const AI_POLL_MS = 3000;
// Polls with no change in «remaining» before the UI gives up on a run.
const AI_MAX_STALLED_POLLS = 10;

// Default order: `sort` ascending puts listings without pictures (sort 1, see
// NO_IMAGE_SORT in backend/lib/marketplace-import.js) after the ones with pictures
// (no sort value), newest first within each group.
const MARKETPLACE_DEFAULT_SORT = 'sort -createdDate';
// Sort keys -> API sort strings. «newest» uses MARKETPLACE_DEFAULT_SORT.
const SORT_OPTIONS = Object.freeze([
    Object.freeze({ key: 'newest', sort: '' }),
    Object.freeze({ key: 'priceAsc', sort: 'price' }),
    Object.freeze({ key: 'priceDesc', sort: '-price' }),
]);

@observer
class MarketplaceGrid extends Component {
    constructor(props) {
        super(props);
        this.state = {
            localList: [],
            limit: 50,
            offset: 0,
            savedArticleIds: new Set(),
            // Toolbar filters
            search: '',
            marketplaceCategory: '',
            sortKey: 'newest',
            showSold: false,
            mineOnly: false,
            savedOnly: false,
            hasMore: false,
            loading: false,
            // Admin: AI category/tags bulk run (POST + poll /api/articles/marketplace/ai-tags)
            aiTagging: false,
            aiProgress: null,
            aiError: '',
        };
        this.searchTimer = null;
        this.aiPollTimer = null;
    }

    openImportDrawer = (previewQueue = false) => {
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightLarge } = appState;
        appState.openDrawer('marketplaceImport', {
            height: drawerHeightLarge,
            previewQueue,
            callback: () => {
                this.loadAll(false);
            },
        }, drawerLevel + 1);
    }

    onClickImport = () => {
        this.openImportDrawer(false);
    }

    // Rows handed over by the Finn bookmarklet in the url hash (lib/finnBookmarklet.js).
    // Each run is merged into the browser-local queue, so several runs add up before one import.
    // Only admins and partners may import; the hash is cleared either way.
    handleFinnImportHash = () => {
        const items = decodeFinnImport(window.location.hash);
        if (!items) {
            return;
        }
        route('/marketplace', true);
        const { userStore } = this.props.stores;
        if (!(userStore.isAdmin || userStore.isPartner)) {
            return;
        }
        if (items.length > 0) {
            saveFinnQueue(mergeFinnQueue(loadFinnQueue(), items));
            this.openImportDrawer(true);
        }
    }

    startAiTagging = async () => {
        this.setState({ aiTagging: true, aiError: '', aiProgress: null });
        const response = await util.fetchApi('/api/articles/marketplace/ai-tags', { method: 'POST' }, {});
        if (response?.status !== 202) {
            this.setState({ aiTagging: false, aiError: response?.message || 'AI tagging failed' });
            return;
        }
        const progress = response.data || {};
        this.setState({ aiProgress: progress });
        if (progress.remaining > 0) {
            this.aiPollTimer = setTimeout(this.pollAiTagging, AI_POLL_MS);
        } else {
            this.setState({ aiTagging: false });
        }
    }

    pollAiTagging = async () => {
        this.aiPollTimer = null;
        const response = await util.fetchApi('/api/articles/marketplace/ai-tags', { method: 'GET' }, {});
        if (response?.status !== 200) {
            this.setState({ aiTagging: false, aiError: response?.message || 'AI tagging failed' });
            return;
        }
        const previous = this.state.aiProgress || {};
        const progress = response.data || {};
        // Stop when done, or when nothing has moved for a while (budget hit or run died).
        const stalled = progress.remaining === previous.remaining
            ? (previous.stalledPolls || 0) + 1
            : 0;
        const finished = !(progress.remaining > 0) || stalled >= AI_MAX_STALLED_POLLS;
        this.setState({ aiProgress: { ...progress, stalledPolls: stalled }, aiTagging: !finished });
        if (finished) {
            this.loadAll(false);
            return;
        }
        this.aiPollTimer = setTimeout(this.pollAiTagging, AI_POLL_MS);
    }

    hasActiveFilters = () => {
        const { search, marketplaceCategory, sortKey, showSold, mineOnly, savedOnly } = this.state;
        return !!search || !!marketplaceCategory || sortKey !== 'newest' || showSold || mineOnly || savedOnly;
    }

    buildFilterParams = () => {
        const { search, marketplaceCategory, sortKey, showSold, mineOnly, savedOnly } = this.state;
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const sortOption = SORT_OPTIONS.find(option => option.key === sortKey) || SORT_OPTIONS[0];
        const params = {};
        if (marketplaceCategory) {
            params.marketplaceCategory = marketplaceCategory;
        }
        if (!showSold) {
            params.excludeSold = 1;
        }
        if (sortOption.sort) {
            params.sort = sortOption.sort;
        }
        // The backend rejects savedOnly combined with search or author, so «Lagrede» wins over those two.
        if (savedOnly) {
            params.savedOnly = 1;
        } else {
            if (search.trim()) {
                params.search = search.trim();
            }
            if (mineOnly && user.id > 0) {
                params.author = user.id;
            }
        }
        return params;
    }

    isReturnFoundationEnabled = () => {
        const { appState } = this.props.stores;
        return parseInt(appState.currentCustomer?.featureFlags?.feedReturnFoundation, 10) === 1;
    }

    loadAll = async (append = false) => {
        const { limit } = this.state;
        const offset = append ? this.state.localList.length : 0;
        const { articleStore } = this.props.stores;
        const returnFoundationEnabled = this.isReturnFoundationEnabled();
        this.returnFoundationEnabled = returnFoundationEnabled;
        const filterParams = this.buildFilterParams();
        this.setState({ loading: true });

        let localList;
        if (returnFoundationEnabled) {
            const response = await util.fetchApi('/api/articles/public/', { method: 'GET' }, {
                hostname: articleStore.currentHostname,
                category: 'marketplace',
                sort: MARKETPLACE_DEFAULT_SORT,
                addData: 'creators,likes,comments,saves',
                limit,
                offset,
                ...filterParams,
            });
            localList = response?.status === 200 ? response.data || [] : [];
            const savedArticleIds = new Set(append ? this.state.savedArticleIds : []);
            (response?.included?.savedArticleIds || []).forEach(id => savedArticleIds.add(Number(id)));
            this.setState({ savedArticleIds });
        } else {
            const { sort, ...queryParams } = filterParams;
            localList = await articleStore.load({
                query: {
                    hostname: articleStore.currentHostname,
                    category: 'marketplace',
                    ...queryParams,
                },
                sort: sort || MARKETPLACE_DEFAULT_SORT,
                limit,
                offset,
                skipUpdate: true,
                addData: ['creators', 'likes', 'comments'],
            }, append);
        }

        const pageList = Array.isArray(localList) ? localList : [];
        const newLocalList = append ? [...this.state.localList, ...pageList] : pageList;
        this.setState({
            localList: newLocalList,
            hasMore: pageList.length >= limit,
            loading: false,
        });
    }

    reloadWithFilters = (patch) => {
        this.setState(patch, () => this.loadAll(false));
    }

    onSearchInput = (e) => {
        const { value } = e.target;
        this.setState({ search: value });
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
        this.searchTimer = setTimeout(() => {
            this.searchTimer = null;
            this.loadAll(false);
        }, SEARCH_DEBOUNCE_MS);
    }

    clearSearch = () => {
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
            this.searchTimer = null;
        }
        this.reloadWithFilters({ search: '' });
    }

    onCategoryClick = (e) => {
        const { category = '' } = e.currentTarget.dataset;
        this.reloadWithFilters({ marketplaceCategory: category });
    }

    onSortChange = (e) => {
        this.reloadWithFilters({ sortKey: e.target.value });
    }

    toggleShowSold = () => {
        this.reloadWithFilters({ showSold: !this.state.showSold });
    }

    toggleMineOnly = () => {
        this.reloadWithFilters({ mineOnly: !this.state.mineOnly, savedOnly: false });
    }

    toggleSavedOnly = () => {
        this.reloadWithFilters({ savedOnly: !this.state.savedOnly, mineOnly: false });
    }

    resetFilters = () => {
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
            this.searchTimer = null;
        }
        this.reloadWithFilters({
            search: '',
            marketplaceCategory: '',
            sortKey: 'newest',
            showSold: false,
            mineOnly: false,
            savedOnly: false,
        });
    }

    loadMore = () => {
        this.loadAll(true);
    }

    // «Vis flere» doubles as a sentinel: when it scrolls into view (400px early) the
    // next page loads by itself. The button stays as a fallback without the observer.
    setLoadMoreRef = (ref) => {
        if (this.loadMoreObserver) {
            this.loadMoreObserver.disconnect();
            this.loadMoreObserver = null;
        }
        if (!ref || !('IntersectionObserver' in window)) {
            return;
        }
        this.loadMoreObserver = new IntersectionObserver((entries) => {
            const { hasMore, loading } = this.state;
            if (entries.some(entry => entry.isIntersecting) && hasMore && !loading) {
                this.loadMore();
            }
        }, { rootMargin: '0px 0px 400px 0px' });
        this.loadMoreObserver.observe(ref);
    }

    handleSavedChange = (articleId, saved) => {
        const savedArticleIds = new Set(this.state.savedArticleIds);
        if (saved) {
            savedArticleIds.add(Number(articleId));
        } else {
            savedArticleIds.delete(Number(articleId));
        }
        this.setState({ savedArticleIds });
    }

    viewItem = (e) => {
        const { id } = e.target.closest('.marketplace-item').dataset;
        route(`/marketplace/${id}`);
    }

    onClickNewItem = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        appState.openDrawer('newArticle', {
            height: drawerHeightMediumLarge,
            isNew: true,
            category: 'marketplace',
            active: 1,
            userOnly: true,
            callback: () => {
                this.loadAll(false);
            },
        }, drawerLevel + 1);
    }

    componentDidMount() {
        this.returnFoundationEnabled = this.isReturnFoundationEnabled();
        this.loadAll();
        this.handleFinnImportHash();
        PubSub.subscribe(topics.REFRESH_PAGE, (msg) => {
            if (msg && msg.page === 'marketplace') {
                this.loadAll();
            }
        });
    }

    componentWillUnmount() {
        PubSub.unsubscribe(topics.REFRESH_PAGE);
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
            this.searchTimer = null;
        }
        if (this.aiPollTimer) {
            clearTimeout(this.aiPollTimer);
            this.aiPollTimer = null;
        }
        if (this.loadMoreObserver) {
            this.loadMoreObserver.disconnect();
            this.loadMoreObserver = null;
        }
    }

    componentDidUpdate() {
        const enabled = this.isReturnFoundationEnabled();
        if (enabled !== this.returnFoundationEnabled) {
            this.returnFoundationEnabled = enabled;
            this.loadAll(false);
        }
    }

    render() {
        const {
            localList, savedArticleIds, search, marketplaceCategory, sortKey, showSold, mineOnly, savedOnly, hasMore, loading,
            aiTagging, aiProgress, aiError,
        } = this.state;
        const { userStore } = this.props.stores;
        const { user = {}, isAdmin, isPartner } = userStore;
        const aiDone = aiProgress && aiProgress.total > 0 ? aiProgress.total - aiProgress.remaining : 0;
        const canImport = isAdmin || isPartner;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const returnFoundationEnabled = this.isReturnFoundationEnabled();
        const isLoggedIn = user.id > 0;
        const filtersActive = this.hasActiveFilters();
        const chipClass = active => `btn btn-sm rounded-pill ${active ? 'btn-primary' : 'btn-outline-secondary'}`;

        return (
            <div class='w-100 px-2 mt-4'>
                {/* Header */}
                <div class='d-flex justify-content-between align-items-center mb-3 px-1'>
                    <h5 class='mb-0'>
                        <i class='fa-duotone fa-store me-2' />
                        <Text id='article.marketplace'>Marketplace</Text>
                    </h5>
                    <div class='d-flex align-items-center gap-2'>
                        {canImport && (
                            <button
                                type='button'
                                class='btn btn-sm btn-outline-secondary rounded-pill'
                                title='Importer annonser fra Excel- eller CSV-fil, eller fra Finn.no'
                                onClick={this.onClickImport}
                            >
                                <i class='fa-duotone fa-file-import me-1' />
                                <Text id='marketplace.import-from-file'>Import from file</Text>
                            </button>
                        )}
                        {isAdmin && (
                            <button
                                type='button'
                                class='btn btn-sm btn-outline-primary rounded-pill'
                                disabled={aiTagging}
                                title='Lar AI sette kategori og tagger på annonser som mangler det'
                                onClick={this.startAiTagging}
                            >
                                {aiTagging ? (
                                    <>
                                        <i class='fa-duotone fa-spinner fa-spin me-1' />
                                        {aiProgress ? `${aiDone} av ${aiProgress.total}` : <Text id='marketplace.ai-tagging'>Tagging...</Text>}
                                    </>
                                ) : (
                                    <>
                                        <i class='fa-solid fa-wand-magic-sparkles me-1' />
                                        <Text id='marketplace.ai-tag-listings'>AI-tag listings</Text>
                                    </>
                                )}
                            </button>
                        )}
                        <button
                            type='button'
                            class='btn btn-sm btn-success rounded-pill'
                            onClick={this.onClickNewItem}
                        >
                            <i class='fa-solid fa-plus me-1' />
                            <Text id='article.add-for-sale'>Sell item</Text>
                        </button>
                    </div>
                </div>
                {isAdmin && aiError && (
                    <div class='alert alert-warning py-2 px-3 mb-3 small'>
                        <i class='fa-solid fa-triangle-exclamation me-1' />
                        {aiError}
                    </div>
                )}

                {/* Toolbar: search, categories, sort, toggles */}
                <div class='d-flex flex-column gap-2 mb-3 px-1'>
                    <div class='input-group input-group-sm'>
                        <span class='input-group-text'><i class='fa-solid fa-magnifying-glass' /></span>
                        <input
                            type='search'
                            class='form-control'
                            placeholder='Søk i rubrikk'
                            value={search}
                            disabled={savedOnly}
                            onInput={this.onSearchInput}
                        />
                        {search && (
                            <button type='button' class='btn btn-outline-secondary' onClick={this.clearSearch} title='Tøm søk'>
                                <i class='fa-solid fa-xmark' />
                            </button>
                        )}
                    </div>
                    <div class='d-flex flex-wrap align-items-center gap-1'>
                        <button type='button' class={chipClass(!marketplaceCategory)} data-category='' onClick={this.onCategoryClick}>
                            <Text id='marketplace.all-categories'>All</Text>
                        </button>
                        {MARKETPLACE_CATEGORIES.map(category => (
                            <button
                                type='button'
                                key={category.key}
                                class={chipClass(marketplaceCategory === category.key)}
                                data-category={category.key}
                                onClick={this.onCategoryClick}
                            >
                                <i class={`${category.icon} me-1`} />
                                {category.label}
                            </button>
                        ))}
                        <select class='form-select form-select-sm w-auto ms-auto' value={sortKey} onChange={this.onSortChange}>
                            <option value='newest'>Nyeste</option>
                            <option value='priceAsc'>Pris lav til høy</option>
                            <option value='priceDesc'>Pris høy til lav</option>
                        </select>
                    </div>
                    <div class='d-flex flex-wrap align-items-center gap-1'>
                        <button type='button' class={chipClass(showSold)} onClick={this.toggleShowSold}>
                            <i class={`fa-solid ${showSold ? 'fa-eye' : 'fa-eye-slash'} me-1`} />
                            <Text id='marketplace.show-sold'>Show sold</Text>
                        </button>
                        {isLoggedIn && (
                            <button type='button' class={chipClass(mineOnly)} onClick={this.toggleMineOnly}>
                                <i class='fa-solid fa-user me-1' />
                                <Text id='marketplace.mine'>My listings</Text>
                            </button>
                        )}
                        {isLoggedIn && returnFoundationEnabled && (
                            <button type='button' class={chipClass(savedOnly)} onClick={this.toggleSavedOnly}>
                                <i class='fa-solid fa-bookmark me-1' />
                                <Text id='marketplace.saved'>Saved</Text>
                            </button>
                        )}
                        {filtersActive && (
                            <button type='button' class='btn btn-sm btn-link text-decoration-none ms-auto' onClick={this.resetFilters}>
                                <i class='fa-solid fa-rotate-left me-1' />
                                <Text id='marketplace.reset-filters'>Reset filters</Text>
                            </button>
                        )}
                        {loading && <i class='fa-duotone fa-spinner fa-spin text-body-secondary ms-2' />}
                    </div>
                </div>

                {/* Grid */}
                {localList && localList.length > 0 ? (
                    <div class='row g-2'>
                        {localList.map(item => {
                            const imageUrl = item.images?.[0]?.s3MediumLink || item.img?.s3MediumLink || item.urlImageUrl;
                            const hasImage = !!imageUrl;
                            const isSold = item.isSold;
                            const isNew = !isSold && isNewListing(item.publishDate);
                            const categoryLabel = marketplaceCategoryLabel(item.marketplaceCategory);
                            const hasFinnLink = isFinnUrl(item.url);

                            return (
                                <div class='col-6 col-md-4 col-lg-3' key={item.id}>
                                    <div
                                        class={`marketplace-item card h-100 ${darkmode ? 'bg-darkmode border-secondary' : ''}`}
                                        data-id={item.id}
                                        onClick={this.viewItem}
                                        style='cursor: pointer; overflow: hidden;'
                                    >
                                        {/* Image */}
                                        <div
                                            class='position-relative bg-body-secondary'
                                            style='aspect-ratio: 1 / 1; overflow: hidden;'
                                        >
                                            {hasImage ? (
                                                <img
                                                    src={imageUrl}
                                                    alt={item.title}
                                                    class='w-100 h-100'
                                                    style={`object-fit: cover; ${isSold ? 'opacity: 0.5;' : ''}`}
                                                />
                                            ) : (
                                                <div class='w-100 h-100 d-flex align-items-center justify-content-center'>
                                                    <i class='fa-duotone fa-image fa-3x text-body-secondary' style='opacity: 0.5;' />
                                                </div>
                                            )}

                                            {/* «Ny» badge for listings under a week old */}
                                            {isNew && (
                                                <span class='position-absolute badge bg-warning text-dark' style='top: 8px; left: 8px;'>
                                                    <Text id='marketplace.new'>New</Text>
                                                </span>
                                            )}

                                            {/* Sold overlay */}
                                            {isSold && (
                                                <div
                                                    class='position-absolute d-flex align-items-center justify-content-center'
                                                    style='top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.3);'
                                                >
                                                    <span class='badge bg-danger' style='font-size: 1rem; transform: rotate(-15deg);'>
                                                        <Text id='article.sold'>Sold</Text>
                                                    </span>
                                                </div>
                                            )}

                                            {returnFoundationEnabled && (
                                                <div class='position-absolute bg-body rounded-circle px-2 py-1' style='top: 8px; right: 8px;'>
                                                    <SaveButton
                                                        stores={this.props.stores}
                                                        articleId={item.id}
                                                        hostname={this.props.stores.articleStore.currentHostname || window.location.hostname}
                                                        saved={savedArticleIds.has(Number(item.id))}
                                                        surface='feed'
                                                        onSavedChange={this.handleSavedChange}
                                                    />
                                                </div>
                                            )}

                                            {/* Price badge */}
                                            <div
                                                class='position-absolute'
                                                style='bottom: 8px; left: 8px;'
                                            >
                                                <span
                                                    class='badge bg-success'
                                                    style='font-size: 0.9rem; font-weight: 600; padding: 6px 10px;'
                                                >
                                                    Kr. {util.format(item.price || 0, 0)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Info */}
                                        <div class='card-body p-2'>
                                            {(() => {
                                                const title = item.title && item.title.toLowerCase() !== 'no title' ? item.title : null;
                                                const displayText = title || item.ingress || item.body?.substring(0, 50);
                                                const hasContent = !!displayText;
                                                return (<>
                                                    {hasContent && (
                                                        <h6
                                                            class='card-title mb-1'
                                                            style='font-size: 0.85rem; font-weight: 600; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;'
                                                        >
                                                            {displayText}
                                                        </h6>
                                                    )}
                                                    <div class='d-flex align-items-center justify-content-between'>
                                                        <small class={hasContent ? 'text-muted' : ''} style={`font-size: ${hasContent ? '0.7rem' : '0.85rem'};`}>
                                                            {item.creator || <Text id='marketplace.unknown'>Unknown</Text>}
                                                        </small>
                                                        <small class='text-muted' style='font-size: 0.65rem;'>
                                                            {util.formatDistance(item.publishDate, new Date(), { textAgo: '' })}
                                                        </small>
                                                    </div>
                                                    {(categoryLabel || hasFinnLink) && (
                                                        <div class='d-flex align-items-center justify-content-between mt-1'>
                                                            <small class='text-body-secondary' style='font-size: 0.65rem;'>
                                                                {categoryLabel}
                                                            </small>
                                                            {hasFinnLink && (
                                                                <small class='text-body-secondary' style='font-size: 0.65rem;' title='Annonsen finnes også på Finn.no'>
                                                                    <i class='fa-solid fa-arrow-up-right-from-square me-1' />Finn
                                                                </small>
                                                            )}
                                                        </div>
                                                    )}
                                                </>);
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div class='text-center text-muted py-5'>
                        <i class={`fa-duotone ${filtersActive ? 'fa-filter-circle-xmark' : 'fa-store'} fa-3x mb-3`} style='opacity: 0.3;' />
                        {filtersActive ? (
                            <>
                                <p><Text id='marketplace.no-match'>No listings match your filters</Text></p>
                                <button
                                    type='button'
                                    class='btn btn-outline-secondary rounded-pill'
                                    onClick={this.resetFilters}
                                >
                                    <i class='fa-solid fa-rotate-left me-1' />
                                    <Text id='marketplace.reset-filters'>Reset filters</Text>
                                </button>
                            </>
                        ) : (
                            <>
                                <p><Text id='marketplace.no-items'>No items for sale yet</Text></p>
                                <button
                                    type='button'
                                    class='btn btn-success rounded-pill'
                                    onClick={this.onClickNewItem}
                                >
                                    <i class='fa-solid fa-plus me-1' />
                                    <Text id='article.be-first-to-sell'>Be the first to sell something!</Text>
                                </button>
                            </>
                        )}
                    </div>
                )}

                {/* Paging */}
                {hasMore && localList.length > 0 && (
                    <div class='text-center mt-3' ref={this.setLoadMoreRef}>
                        <button
                            type='button'
                            class='btn btn-outline-secondary rounded-pill'
                            disabled={loading}
                            onClick={this.loadMore}
                        >
                            {loading ? <i class='fa-duotone fa-spinner fa-spin me-1' /> : <i class='fa-solid fa-angles-down me-1' />}
                            <Text id='marketplace.load-more'>Show more</Text>
                        </button>
                    </div>
                )}
            </div>
        );
    }
}

export default MarketplaceGrid;
